import React, { useContext, useEffect, useState } from "react";
import { Title, Content, ButtonsContainter, HeaderContainer, EditButton, ErrorMsg } from "./style";
import { useAppSelector } from "../../../../../app/hooks";
import { NetworkErrorAlertContext } from "../../../../../context/NetworkErrorAlert";
import { SuccessAlertContext } from "../../../../../context/SuccessAlertContext";
import { Button } from "../../../../../components/common/Button";
import { createAndUpdateWireBeneficiary, editWireBeneficiary } from "../../../wiresAPI";
import { Controller, useForm } from "react-hook-form";
import { Input } from "../../../../../components/common/Input";
import { countryOptions } from "../../../SendWire/components/Beneficiary/BeneficiaryModal/utils";
import CustomSelect from "../../../../../components/common/CustomSelect";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { FEATURE_FLAG_KEYS } from "../../../../../utils/featureFlags";
import { useNGNBankAccountResolution } from "../../../../../app/hooks/useNGNBankAccountResolution";

type FormData = {
  beneficiaryName: string;
  businessName: string;
  country: string;
  bankName: string;
  bankAddress: string;
  phone: string;
  email: string;
  address: string;
  iban: string;
  swiftCode: string;
  accountNumber: string;
  token: string;
};

type FieldItemsObject = {
  id: number;
  title: string;
  value: string;
  field: keyof FormData;
  editable: boolean;
  rules?: {
    required?: string;
    pattern?: {
      value: RegExp;
      message: string;
    };
  };
  type: "text" | "number" | "password" | "email" | 'date' | "tel";
}

const ManageBeneficiaryModal = ({
  recipient,
  setShowModal,
  setLoading,
  getBeneficiaries,
  onCancel
}: {
  recipient: any;
  setShowModal?: (arg: boolean) => void;
  setLoading?: (arg: boolean) => void;
  getBeneficiaries?: (id: number) => void;
  onCancel?: () => void
}) => {
  const { onShowAlert: onShowErrorAlert } = useContext(
    NetworkErrorAlertContext
  );
  const { onShowAlert: onShowSuccessAlert } = useContext(SuccessAlertContext);
  const { businessId } = useAppSelector((state) => state.users);
  const [ isEditingBeneficiary, setIsEditingBeneficiary ] = useState(false)

  const enableEditBeneficiary = useFeatureFlagEnabled(FEATURE_FLAG_KEYS.enableEditBeneficiary)

  const formReset = () => ({
    beneficiaryName: recipient?.beneficiaryName || '',
    businessName: recipient?.name || '',
    country: recipient?.country || '',
    bankName: recipient?.bankName || '',
    bankAddress: recipient?.bankAddress || '',
    accountNumber: recipient?.accountNumber || '',
    phone: recipient?.phone || '',
    email: recipient?.email || '',
    address: recipient?.address || '',
    iban: recipient?.iban || '',
    swiftCode: recipient?.swiftCode || '',
    token: recipient?.token || ''
  });

 const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
    watch, 
    setValue,
  } = useForm<FormData>({
    defaultValues: formReset()
  });

  const selectedCountry = watch("country")
  const isNigerianBankAccount = selectedCountry === "Nigeria"
  const bankName = watch("bankName");
  const accountNumber = watch("accountNumber")

  const items: FieldItemsObject[] = [
    {
      id: 1,
      title: "Business name",
      value: recipient?.name,
      field: 'businessName',
      editable: true,
      rules: { required: "Business name is required" },
      type: 'text'
    },
    {
      id: 2,
      title: "Country",
      value: recipient?.country,
      field: 'country',
      editable: true,
      rules: { required: "Country is required" },
      type: 'text'
    },
    {
      id: 3,
      title: "Bank name",
      value: recipient?.bankName,
      field: 'bankName',
      editable: true,
      rules: { required: "Bank name is required" },
      type: 'text'
    },
    {
      id: 4,
      title: "Account number",
      value: recipient?.accountNumber,
      field: 'accountNumber',
      editable: true,
      rules: { required: "Account number is required" },
      type: 'number'
    },
    {
      id: 5,
      title: "Beneficiary name",
      value: recipient?.beneficiaryName,
      field: 'beneficiaryName',
      editable: true,
      rules: { required: "Beneficiary name is required" },
      type: 'text',
    },
    {
      id: 6,
      title: "Bank address",
      value: recipient?.bankAddress,
      field: 'bankAddress',
      editable: true,
      rules: { required: "Bank address is required" },
      type: 'text'
    },
    {
      id: 7,
      title: "Phone number",
      value: recipient?.phone,
      field: 'phone',
      editable: true,
      type: 'tel'
    },
    {
      id: 8,
      title: "Beneficiary email",
      value: recipient?.email,
      field: 'email',
      editable: true,
      rules: {
        required: "Beneficiary email is required",
        pattern: {
          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
          message: 'Invalid email address'
        }
      },
      type: 'email'
    },
    {
      id: 9,
      title: "Beneficiary address",
      value: recipient?.address,
      field: 'address',
      editable: true,
      rules: { required: "Beneficiary address is required" },
      type: 'text'
    },
    {
      id: 10,
      title: "IBAN",
      value: recipient?.iban,
      field: 'iban',
      editable: true,
      type: 'number'
    },
    {
      id: 11,
      title: "Swift code",
      value: recipient?.swiftCode,
      field: 'swiftCode',
      editable: true,
      type: 'number'
    },
    {
      id: 12,
      title: "Token",
      value: recipient?.token,
      field: 'token',
      editable: false,
      type: 'text'
    }
  ]

  const isEditingNigerianBeneficiary = isNigerianBankAccount && isEditingBeneficiary
  
  const { mappedBankList } = useNGNBankAccountResolution({
    isEnabled: isEditingNigerianBeneficiary,
    countryCode: 'NG',
    bankName,
    accountNumber,
    onAccountNameResolved: (accountName) => {
      setValue('beneficiaryName', accountName, { shouldValidate: true });
    },
    onError: (error) => {
      onShowErrorAlert(error, error.message);
    },
    onLoading: setLoading
  });

  useEffect(() => {
    reset(formReset());
    setIsEditingBeneficiary(false)
  }, [recipient, reset]);

  const handleEditToggle = () => {
    reset(formReset())
    setIsEditingBeneficiary(!isEditingBeneficiary)
  };

   const onSaveChanges = async (formData) => {
    const country = formData.country
    const code = countryOptions.find((c) => c.label === country)

    const payload = {
      name: formData.businessName,
      beneficiaryName: formData.beneficiaryName,
      country: formData.country,
      countryCode: code?.code,
      bankName: formData.bankName,
      accountNumber: formData.accountNumber,
      phone: formData.phone,
      email: formData.email,
      iban: formData.iban,
      swiftCode: formData.swiftCode,
      business: businessId,
      address: formData.address,
      bankAddress: formData.bankAddress,
      id: recipient.id,
      vfxId: recipient.vfxId,
      disabled: recipient.disabled,
    };

    try {
      setLoading?.(true);
      await editWireBeneficiary(payload);
      setLoading?.(false);
      onCancel()
      onShowSuccessAlert("Beneficiary updated successfully");
      getBeneficiaries?.(businessId);
    } catch (err) {
      setLoading?.(false);
      onShowErrorAlert(err, err.message);
    }
  };

  const onSubmit = async () => {
    const payload = {
      name: recipient.name,
      beneficiaryName: recipient.beneficiaryName,
      country: recipient.country,
      countryCode: recipient.countryCode,
      bankName: recipient.bankName,
      accountNumber: recipient.accountNumber,
      phone: recipient.phone,
      email: recipient.email,
      iban: recipient.iban,
      swiftCode: recipient.swiftCode,
      business: businessId,
      address: recipient.address,
      bankAddress: recipient.bankAddress,
      id: recipient.id,
      vfxId: recipient.vfxId,
      disabled: !recipient.disabled,
    };

    try {
      setLoading(true);
      await createAndUpdateWireBeneficiary(payload);
      setLoading(false)
      setShowModal(false)
      onCancel()
      onShowSuccessAlert("Beneficiary updated successfully");
      getBeneficiaries(businessId)
    } catch (err) {
      setLoading(false)
      onShowErrorAlert(err, err.message);
    }
  };

  const ModalTitle = isEditingBeneficiary ? "Edit beneficiary" : "Manage beneficiary"

  return (
    <div>
      <HeaderContainer>
        <Title>{ModalTitle}</Title>
        {enableEditBeneficiary && 
          <EditButton onClick={handleEditToggle}>{isEditingBeneficiary ? "Cancel" : "Edit"}</EditButton>
        }
      </HeaderContainer>
      
      <form onSubmit={handleSubmit(onSaveChanges)}>
        <Content>
          {items.map((item) => (
            <div>
              <span>{item.title}</span>
              <div>
                <Controller
                  name={item.field}
                  control={control}
                  rules={item.rules}
                  render={({ field }) => (
                    <>
                      {item.title === 'Country' || (item.field === 'bankName' && isNigerianBankAccount) ? 
                        <CustomSelect
                          placeholder="Country"
                          options={item.title === 'Country' ? countryOptions : mappedBankList}
                          $width="100%"
                          $height="46px"
                          field={field}
                          showSearch
                          lightBorder
                          disabled={recipient?.disabled || !isEditingBeneficiary || !item.editable}
                          disableLabel={true}
                        /> 
                      : <Input
                          {...field}
                          type={item.type}
                          disabled={recipient?.disabled || (item.field === 'beneficiaryName' && isNigerianBankAccount)}
                          hasError={!!errors[item.field]}
                          readonly={!isEditingBeneficiary || !item.editable}
                          disableLabel={true}
                        />
                      }
                    </>
                  )}
                />
                {errors[item.field] && (
                  <ErrorMsg>
                    {errors[item.field].message}
                  </ErrorMsg>
                )}
              </div>
            </div>
          ))}
        </Content>

        <ButtonsContainter>
          <Button
            theme="alternate"
            height="40px"
            fontSize="12px"
            label="Cancel"
            onClick={onCancel}
          />
          {isEditingBeneficiary ? (
            <Button
              theme="secondary"
              height="40px"
              fontSize="12px"
              label="Update beneficiary"
              type="submit"
              disabled={!isDirty}
            />
          ) : (
            <Button
              theme="secondary"
              height="40px"
              fontSize="12px"
              label={`${recipient.disabled ? "Enable" : "Disable"} beneficiary`}
              onClick={onSubmit}
            />
          )}
        </ButtonsContainter>
      </form>

     
    </div>
  );
};

export default ManageBeneficiaryModal;
